import { inject } from 'vue'
import { useNotification } from '@/composables/useNotification.js'
import { getErrorMessage } from '@/utils/error.js'
import { marketHasAssemblyOverServiceHubEnabled } from '@/utils/order.js'
import { useShipment } from '@/composables/useShipment.js'
import { MODE } from '@/constants/device.js'
import { useDeviceStore } from '@/stores/device'

export function useMarket() {
  const cl = inject('cl')
  const Sentry = inject('Sentry')
  const supabase = inject('supabase')
  const { fetchAvailableAllShippingMethods } = useShipment()
  const deviceStore = useDeviceStore()

  async function getCityVouchers(marketId) {
    const market = await cl.markets.retrieve(marketId, {
      fields: {
        markets: ['metadata']
      }
    })

    return market.metadata?.city_vouchers?.[0] ? market.metadata.city_vouchers : false
  }

  /**
   * Get markets list with `price_list` and `merchant.address` included and
   * filtered by given `filterName` and `term`.
   * Also removes entry with code BRN1.
   *
   * @param {string} filterName - name of filter to apply (e.g. name_or_code_i_cont_any)
   * @param {string} term - term to filter by
   * @return {Promise<Array>} - list of markets
   */
  async function getMarketsList(filterName, term) {
    const notification = useNotification()

    try {
      // CL metadata_jcont is JSON-containment (exact match, AND only), so "status=OPEN OR
      // keepOpenInWings is set" can't be one query, and we can't match case-insensitively. Run one
      // query per metadata variant in parallel and merge: open stores, plus closed stores explicitly
      // kept selectable in Wings. The sync may write keepOpenInWings as "True"/"true"/boolean true,
      // so cover each representation.
      const metadataFilters = [
        '{"status":"OPEN"}',
        '{"keepOpenInWings":"True"}',
        '{"keepOpenInWings":"true"}',
        '{"keepOpenInWings":true}'
      ]
      const resultSets = await Promise.all(
        metadataFilters.map((metadata_jcont) =>
          cl.markets.list({
            include: ['price_list', 'merchant.address'],
            filters: {
              [filterName]: `${term}`,
              metadata_jcont
            }
          })
        )
      )
      // merge + dedupe by id (a store may match more than one query)
      const marketsById = new Map()
      resultSets.flat().forEach((market) => marketsById.set(market.id, market))
      // remove entry where code === BRN1
      const markets = [...marketsById.values()].filter((market) => market.code !== 'BRN1')
      return markets
    } catch (error) {
      notification.showNotification({
        message: getErrorMessage(error),
        variant: 'danger'
      })
    }
  }

  /**
   * Gets the market settings from the given warehouse name.
   *
   * @param {string} wh - warehouse name
   * @return {Promise<Object>} - market settings
   *
   * The returned object has the following properties:
   * {
   *   stockLocationId: string,
   *   storeAddressId: string,
   *   warehouse: string,
   *   country: string,
   *   currency: string,
   *   language: string,
   *   marketId: string,
   *   name: string,
   *   serviceHubId: string,
   *   assemblyOnlyInServiceHub: boolean,
   *   storeType: string|null (e.g. 'Internal'; null = normal store),
   *   keepOpenInWings: boolean (closed store kept selectable as a market in Wings),
   *   tags: Array<string>,
   *   mode: string (STORE or SERVICE_HUB),
   *   availableShippingMethods: Array<string>,
   *   cityVouchers: boolean
   * }
   */
  async function getMarketSettingsFromWarehouse(wh) {
    try {
      const searchRes = await getMarketsList('name_eq', wh)
      if (searchRes?.length) {
        const warehouse = searchRes[0]
        const tagsList = await cl.tags.list()
        const stockLocations = await cl.stock_locations.list({
          include: ['address'],
          filters: {
            code_eq: warehouse.code
          }
        })
        const shippingMethods = await fetchAvailableAllShippingMethods()
        const countryCode = warehouse?.merchant?.address?.country_code ?? 'NaN'
        const serviceHubId = await cl.stock_locations.list({
          filters: {
            code_eq: `SH${countryCode.toUpperCase()}`
          }
        })

        let mode = MODE.STORE
        if (['SHAT', 'SHCH', 'SHDE'].includes(warehouse.code)) {
          mode = MODE.SERVICE_HUB
        }

        const settings = {}
        settings.stockLocationId = stockLocations[0].id
        settings.storeAddressId = stockLocations[0].address.id
        settings.warehouse = warehouse.code
        settings.country = warehouse?.merchant?.address?.country_code.toLowerCase()
        settings.currency = warehouse?.price_list?.currency_code
        settings.language = warehouse?.metadata.language.toLowerCase()
        settings.marketId = warehouse?.id
        settings.name = warehouse?.name
        settings.serviceHubId = serviceHubId[0]?.id
        settings.assemblyOnlyInServiceHub = marketHasAssemblyOverServiceHubEnabled(warehouse)
        settings.storeType = warehouse?.metadata?.store_type ?? null
        // sync may store this as "True"/"true"/boolean — normalize to a real boolean
        settings.keepOpenInWings =
          String(warehouse?.metadata?.keepOpenInWings).toLowerCase() === 'true'
        settings.tags = tagsList
        settings.mode = mode
        settings.availableShippingMethods = shippingMethods
        settings.cityVouchers = warehouse?.metadata?.city_vouchers
          ? warehouse.metadata.city_vouchers
          : false

        // check if the current employee is a store employee
        // On the very first login the user is not set in the store_employees table
        if (deviceStore.settings?.currentEmployee?.id) {
          await supabase
            .from('store_employees')
            .upsert({
              store_warehouse: warehouse.code,
              employee_id: deviceStore.settings?.currentEmployee?.id,
              app_environment: import.meta.env.VITE_WEB_ENV
            })
            .select()
        }
        return settings
      }
    } catch (e) {
      throw new Error(e)
    }
  }

  /**
   * Returns the list of market codes whose metadata.store_type is 'Internal'.
   * Used to exclude internal stock locations (which share the market code) from
   * pickup/availability lists, since store_type lives on the market, not the
   * stock_location resource.
   *
   * @return {Promise<Array<string>>} - list of internal store codes
   */
  async function getInternalStoreCodes() {
    try {
      const markets = await cl.markets.list({
        fields: { markets: ['code', 'metadata'] },
        filters: { metadata_jcont: '{"store_type":"Internal"}' },
        pageSize: 25
      })
      return markets.map((market) => market.code)
    } catch (error) {
      Sentry.captureException(error)
      return []
    }
  }

  // use this function to check if the market has assembly over service hub enabled not from settings
  async function getMarketAssemblyOnlyInServiceHub(marketId) {
    try {
      const market = await cl.markets.retrieve(marketId, {
        fields: {
          markets: ['metadata']
        }
      })
      return marketHasAssemblyOverServiceHubEnabled(market)
    } catch (error) {
      Sentry.captureException(error)
      return false
    }
  }

  return {
    getCityVouchers,
    getMarketsList,
    getMarketSettingsFromWarehouse,
    getInternalStoreCodes,
    getMarketAssemblyOnlyInServiceHub
  }
}
